// src/modules/app-views/products/pages/Document/tabs/AiInfoTab/InsuranceDataSection.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Flex, Descriptions, Collapse, Tag, Typography, theme } from 'antd';
import {
  SafetyCertificateOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileProtectOutlined,
  TeamOutlined,
  FormOutlined,
  StarOutlined,
  ToolOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';
import translateX from 'utils/translateX';

const { Text } = Typography;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const InsuranceDataSection = ({ data, isMobile }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const ins = data?.insuranceData;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const hasData = ins && (ins.modalidade || ins.insurer || ins.coverages?.length || ins.exclusions?.length || ins.advantages?.length);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HELPERS
  // ─────────────────────────────────────────────────────────────────────────────
  const formatCurrency = value => {
    if (value === undefined || value === null) return '-';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!hasData) return null;

  return (
    <>
      {/* GENERAL INFO */}
      <Collapse
        className="collapsed-content-fit"
        collapsible="icon"
        defaultActiveKey={['insurance-info']}
        style={{ marginBottom: 16 }}
        items={[
          {
            key: 'insurance-info',
            label: (
              <Flex align="center" gap={8}>
                <SafetyCertificateOutlined style={{ color: token.colorPrimary }} />
                <span style={{ fontWeight: 600 }}>{translateX('insurance_info')}</span>
              </Flex>
            ),
            showArrow: false,
            children: (
              <Descriptions
                bordered
                size="small"
                column={1}
                layout={isMobile ? 'vertical' : 'horizontal'}
                styles={{ label: { fontWeight: 500 } }}
              >
                {ins.modalidade && (
                  <Descriptions.Item label={translateX('modality')}>
                    <Tag color="blue">{ins.modalidade.toUpperCase()}</Tag>
                  </Descriptions.Item>
                )}
                {ins.insurer && (
                  <Descriptions.Item label={translateX('insurer')}>
                    {ins.insurer}
                  </Descriptions.Item>
                )}
                {ins.insurerDocument && (
                  <Descriptions.Item label={translateX('insurer_document')}>
                    {ins.insurerDocument}
                  </Descriptions.Item>
                )}
                {ins.susepProcess && (
                  <Descriptions.Item label={translateX('susep_process')}>
                    {ins.susepProcess}
                  </Descriptions.Item>
                )}
              </Descriptions>
            ),
          },
        ]}
      />

      {/* COVERAGES */}
      {ins.coverages?.length > 0 && (
        <Collapse
          className="collapsed-content-fit"
          collapsible="icon"
          defaultActiveKey={['coverages']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'coverages',
              label: (
                <Flex align="center" gap={8}>
                  <CheckCircleOutlined style={{ color: token.colorSuccess }} />
                  <span style={{ fontWeight: 600 }}>{translateX('coverages')}</span>
                  <Tag>{ins.coverages.length}</Tag>
                </Flex>
              ),
              showArrow: false,
              children: (
                <div style={{ padding: 16 }}>
                  {ins.coverages.map((cov, i) => (
                    <div
                      key={i}
                      style={{
                        marginBottom: i < ins.coverages.length - 1 ? 12 : 0,
                        padding: '10px 12px',
                        borderRadius: 8,
                        backgroundColor: 'var(--ant-color-fill-quaternary)',
                      }}
                    >
                      <Flex align="center" gap={8} style={{ marginBottom: 4 }}>
                        {cov.code && <Tag color="blue" style={{ margin: 0 }}>{cov.code}</Tag>}
                        <Text strong style={{ fontSize: 13 }}>{cov.name}</Text>
                      </Flex>
                      {cov.description && (
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                          {cov.description}
                        </Text>
                      )}
                      <Flex gap={16} style={{ marginTop: 6 }}>
                        {cov.limit && (
                          <Text style={{ fontSize: 12 }}>
                            <strong>{translateX('limit')}:</strong> {cov.limit}
                          </Text>
                        )}
                        {cov.deductible && (
                          <Text style={{ fontSize: 12 }}>
                            <strong>{translateX('deductible')}:</strong> {cov.deductible}
                          </Text>
                        )}
                      </Flex>
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      )}

      {/* INCLUDED SERVICES */}
      {ins.includedServices?.length > 0 && (
        <Collapse
          className="collapsed-content-fit"
          collapsible="icon"
          defaultActiveKey={['included-services']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'included-services',
              label: (
                <Flex align="center" gap={8}>
                  <ToolOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('included_services')}</span>
                  <Tag>{ins.includedServices.length}</Tag>
                </Flex>
              ),
              showArrow: false,
              children: (
                <div style={{ padding: 16 }}>
                  {ins.includedServices.map((svc, i) => (
                    <div
                      key={i}
                      style={{
                        marginBottom: i < ins.includedServices.length - 1 ? 12 : 0,
                        padding: '10px 12px',
                        borderRadius: 8,
                        backgroundColor: 'var(--ant-color-fill-quaternary)',
                      }}
                    >
                      <Flex align="center" gap={8}>
                        {svc.code && <Tag style={{ margin: 0 }}>{svc.code}</Tag>}
                        <Text strong style={{ fontSize: 13 }}>{svc.name}</Text>
                      </Flex>
                      {svc.description && (
                        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                          {svc.description}
                        </Text>
                      )}
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      )}

      {/* POLICY DETAILS */}
      {ins.policyDetails && (
        <Collapse
          className="collapsed-content-fit"
          collapsible="icon"
          defaultActiveKey={['policy-details']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'policy-details',
              label: (
                <Flex align="center" gap={8}>
                  <FileProtectOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('policy_details')}</span>
                </Flex>
              ),
              showArrow: false,
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  {ins.policyDetails.validityMonths !== undefined && (
                    <Descriptions.Item label={translateX('validity_months')}>
                      {ins.policyDetails.validityMonths} {translateX('months')}
                    </Descriptions.Item>
                  )}
                  {ins.policyDetails.renewalType && (
                    <Descriptions.Item label={translateX('renewal_type')}>
                      <Tag>{ins.policyDetails.renewalType.toUpperCase()}</Tag>
                    </Descriptions.Item>
                  )}
                  {ins.policyDetails.gracePeriodDays !== undefined && (
                    <Descriptions.Item label={translateX('grace_period_days')}>
                      {ins.policyDetails.gracePeriodDays} {translateX('days')}
                    </Descriptions.Item>
                  )}
                  {ins.policyDetails.calculationBase && (
                    <Descriptions.Item label={translateX('calculation_base')}>
                      <Tag color="blue">{ins.policyDetails.calculationBase.toUpperCase()}</Tag>
                    </Descriptions.Item>
                  )}
                  {ins.policyDetails.premiumRange && (
                    <Descriptions.Item label={translateX('premium_range')}>
                      {formatCurrency(ins.policyDetails.premiumRange.min)} ~ {formatCurrency(ins.policyDetails.premiumRange.max)}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* COMMISSIONS */}
      {ins.commissions && (ins.commissions.rate !== undefined || ins.commissions.paymentType) && (
        <Collapse
          className="collapsed-content-fit"
          collapsible="icon"
          defaultActiveKey={['commissions']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'commissions',
              label: (
                <Flex align="center" gap={8}>
                  <TeamOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('commissions')}</span>
                </Flex>
              ),
              showArrow: false,
              children: (
                <Descriptions
                  bordered
                  size="small"
                  column={1}
                  layout={isMobile ? 'vertical' : 'horizontal'}
                  styles={{ label: { fontWeight: 500 } }}
                >
                  {ins.commissions.rate !== undefined && (
                    <Descriptions.Item label={translateX('commission_rate')}>
                      <Tag color="green">{ins.commissions.rate}%</Tag>
                    </Descriptions.Item>
                  )}
                  {ins.commissions.bonusRate !== undefined && ins.commissions.bonusRate !== null && (
                    <Descriptions.Item label={translateX('bonus_rate')}>
                      <Tag color="gold">{ins.commissions.bonusRate}%</Tag>
                    </Descriptions.Item>
                  )}
                  {ins.commissions.bonusConditions && (
                    <Descriptions.Item label={translateX('bonus_conditions')}>
                      {ins.commissions.bonusConditions}
                    </Descriptions.Item>
                  )}
                  {ins.commissions.paymentType && (
                    <Descriptions.Item label={translateX('payment_type')}>
                      {ins.commissions.paymentType}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ),
            },
          ]}
        />
      )}

      {/* EXCLUSIONS */}
      {ins.exclusions?.length > 0 && (
        <Collapse
          className="collapsed-content-fit"
          collapsible="icon"
          defaultActiveKey={['exclusions']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'exclusions',
              label: (
                <Flex align="center" gap={8}>
                  <CloseCircleOutlined style={{ color: token.colorError }} />
                  <span style={{ fontWeight: 600 }}>{translateX('exclusions')}</span>
                  <Tag>{ins.exclusions.length}</Tag>
                </Flex>
              ),
              showArrow: false,
              children: (
                <div style={{ padding: 16 }}>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {ins.exclusions.map((exc, i) => (
                      <li key={i} style={{ marginBottom: 4, fontSize: 13 }}>{exc}</li>
                    ))}
                  </ul>
                </div>
              ),
            },
          ]}
        />
      )}

      {/* ADVANTAGES */}
      {ins.advantages?.length > 0 && (
        <Collapse
          className="collapsed-content-fit"
          collapsible="icon"
          defaultActiveKey={['advantages']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'advantages',
              label: (
                <Flex align="center" gap={8}>
                  <StarOutlined style={{ color: token.colorWarning }} />
                  <span style={{ fontWeight: 600 }}>{translateX('advantages')}</span>
                  <Tag>{ins.advantages.length}</Tag>
                </Flex>
              ),
              showArrow: false,
              children: (
                <div style={{ padding: 16 }}>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {ins.advantages.map((adv, i) => (
                      <li key={i} style={{ marginBottom: 4, fontSize: 13 }}>{adv}</li>
                    ))}
                  </ul>
                </div>
              ),
            },
          ]}
        />
      )}

      {/* QUOTE FIELDS */}
      {ins.quoteFields?.length > 0 && (
        <Collapse
          className="collapsed-content-fit"
          collapsible="icon"
          defaultActiveKey={['quote-fields']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'quote-fields',
              label: (
                <Flex align="center" gap={8}>
                  <FormOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('quote_fields')}</span>
                  <Tag>{ins.quoteFields.length}</Tag>
                </Flex>
              ),
              showArrow: false,
              children: (
                <div style={{ padding: 16 }}>
                  {ins.quoteFields.map((field, i) => (
                    <div
                      key={i}
                      style={{
                        marginBottom: i < ins.quoteFields.length - 1 ? 8 : 0,
                        padding: '8px 12px',
                        borderRadius: 8,
                        backgroundColor: 'var(--ant-color-fill-quaternary)',
                      }}
                    >
                      <Flex align="center" gap={8} wrap="wrap">
                        <Text strong style={{ fontSize: 13 }}>{field.label}</Text>
                        <Tag color="blue" style={{ margin: 0 }}>{field.type}</Tag>
                        {field.required && <Tag color="red" style={{ margin: 0 }}>{translateX('required')}</Tag>}
                      </Flex>
                      {field.options?.length > 0 && (
                        <Flex gap={4} wrap="wrap" style={{ marginTop: 4 }}>
                          {field.options.map((opt, j) => (
                            <Tag key={j} style={{ margin: 0, fontSize: 11 }}>{opt}</Tag>
                          ))}
                        </Flex>
                      )}
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      )}

      {/* MATERIALS */}
      {ins.materials?.length > 0 && (
        <Collapse
          className="collapsed-content-fit"
          collapsible="icon"
          defaultActiveKey={['materials']}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'materials',
              label: (
                <Flex align="center" gap={8}>
                  <PaperClipOutlined style={{ color: token.colorPrimary }} />
                  <span style={{ fontWeight: 600 }}>{translateX('materials')}</span>
                  <Tag>{ins.materials.length}</Tag>
                </Flex>
              ),
              showArrow: false,
              children: (
                <div style={{ padding: 16 }}>
                  {ins.materials.map((mat, i) => (
                    <div
                      key={i}
                      style={{
                        marginBottom: i < ins.materials.length - 1 ? 8 : 0,
                        padding: '8px 12px',
                        borderRadius: 8,
                        backgroundColor: 'var(--ant-color-fill-quaternary)',
                      }}
                    >
                      <Flex align="center" gap={8}>
                        <Text strong style={{ fontSize: 13 }}>{mat.name}</Text>
                        {mat.type && <Tag style={{ margin: 0 }}>{mat.type}</Tag>}
                      </Flex>
                      {mat.url && (
                        <a href={mat.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}>
                          {mat.url}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      )}
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default InsuranceDataSection;
